/**
 * VP-175 — panel „przejrzyj i uruchom" wizarda.
 *
 * Ostatni ekran obu trybów (URL/AI i ręcznego). User widzi prostą reklamę
 * (zdjęcie + nagłówek + tekst + budżet), może poprawić pola i klika
 * „Uruchom reklamę" — launch dzieje się TUTAJ (SSE do /launch), bez
 * przerzucania do CampaignBuildera. Zero pytań o cele, KPI czy wskaźniki.
 */
import { useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  ImagePlus,
  Loader2,
  PartyPopper,
  Rocket,
  XCircle,
} from "lucide-react";
import {
  buildWizardLaunchBody,
  launchAdOverSSE,
  uploadCreativeFile,
  type LaunchStepEvent,
  type WizardCreative,
  type WizardLaunchResult,
} from "./wizard-launch";
import type { MetaReadinessContext } from "./meta-readiness";

export type AdReviewPrefill = {
  campaignName?: string;
  linkUrl?: string;
  headline?: string;
  primaryText?: string;
  dailyBudget?: number;
  /** Propozycje grafik (np. ze strony usera w trybie AI). */
  suggestedImages?: string[];
};

type Props = {
  ctx: MetaReadinessContext;
  prefill: AdReviewPrefill;
  onBack: () => void;
  /** „Stwórz kolejną reklamę" na ekranie sukcesu. */
  onRestart: () => void;
};

type PanelPhase = "edit" | "launching" | "success";

function defaultCampaignName(linkUrl: string): string {
  let domain = "";
  try {
    domain = new URL(/^https?:\/\//i.test(linkUrl) ? linkUrl : `https://${linkUrl}`).hostname;
  } catch {
    /* zła nazwa hosta — zostaje pusto */
  }
  const date = new Date().toLocaleDateString("pl-PL");
  return domain ? `Reklama — ${domain} (${date})` : `Reklama — ${date}`;
}

function LaunchProgress({ steps }: { steps: LaunchStepEvent[] }): JSX.Element {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-2.5">
      {steps.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-white/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Przygotowuję uruchomienie…
        </div>
      )}
      {steps.map((s) => (
        <div key={s.step} className="flex items-center gap-2 text-sm">
          {s.status === "done" ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          ) : s.status === "error" ? (
            <XCircle className="w-4 h-4 text-red-400 shrink-0" />
          ) : (
            <Loader2 className="w-4 h-4 animate-spin text-[#ef3e29] shrink-0" />
          )}
          <span className={s.status === "error" ? "text-red-300" : "text-white/70"}>
            {s.message || s.step}
          </span>
        </div>
      ))}
    </div>
  );
}

function SuccessScreen({
  result,
  onRestart,
}: {
  result: WizardLaunchResult;
  onRestart: () => void;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-6 text-center space-y-3">
      <PartyPopper className="w-10 h-10 text-emerald-400 mx-auto" />
      <div className="text-lg font-semibold text-white">Twoja reklama została uruchomiona!</div>
      <p className="text-sm text-white/60 max-w-md mx-auto">
        Facebook sprawdzi ją teraz automatycznie (zwykle trwa to do 24 godzin) i zacznie wyświetlać.
        Możesz ją w każdej chwili wstrzymać.
      </p>
      <div className="flex items-center justify-center gap-3 pt-1">
        {result.adsManagerUrl && (
          <a href={result.adsManagerUrl} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" className="border-white/15">
              Zobacz reklamę na Facebooku <ExternalLink className="w-3 h-3 ml-1.5" />
            </Button>
          </a>
        )}
        <Button
          size="sm"
          onClick={onRestart}
          className="bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white"
        >
          Stwórz kolejną reklamę
        </Button>
      </div>
    </div>
  );
}

export default function AdReviewPanel({ ctx, prefill, onBack, onRestart }: Props): JSX.Element {
  const { toast } = useToast();

  const [phase, setPhase] = useState<PanelPhase>("edit");
  const [linkUrl, setLinkUrl] = useState(prefill.linkUrl ?? "");
  const [headline, setHeadline] = useState(prefill.headline ?? "");
  const [primaryText, setPrimaryText] = useState(prefill.primaryText ?? "");
  const [dailyBudget, setDailyBudget] = useState(String(prefill.dailyBudget ?? 50));

  const suggested = prefill.suggestedImages ?? [];
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>(suggested[0] ?? "");
  const [uploaded, setUploaded] = useState<WizardCreative[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [launchSteps, setLaunchSteps] = useState<LaunchStepEvent[]>([]);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [result, setResult] = useState<WizardLaunchResult | null>(null);

  const allImages: WizardCreative[] = [
    ...suggested.map((url, i) => ({ url, type: "image" as const, name: `propozycja-${i + 1}` })),
    ...uploaded,
  ];
  const selectedCreative = allImages.find((c) => c.url === selectedImageUrl) ?? null;

  const budgetNumber = Number(dailyBudget);
  const canLaunch =
    !!linkUrl.trim() &&
    !!headline.trim() &&
    !!primaryText.trim() &&
    budgetNumber > 0 &&
    !!selectedCreative;

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const creative = await uploadCreativeFile(files[0]);
      setUploaded((prev) => [...prev, creative]);
      setSelectedImageUrl(creative.url);
    } catch (err) {
      toast({
        title: "Nie udało się wgrać pliku",
        description: err instanceof Error ? err.message : "Spróbuj ponownie.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const recordStep = (event: LaunchStepEvent): void => {
    setLaunchSteps((prev) => {
      const idx = prev.findIndex((p) => p.step === event.step);
      if (idx >= 0) return prev.map((p, i) => (i === idx ? event : p));
      return [...prev, event];
    });
  };

  const handleLaunch = async (): Promise<void> => {
    if (!canLaunch || !selectedCreative) return;
    setPhase("launching");
    setLaunchError(null);
    setLaunchSteps([]);
    try {
      const body = buildWizardLaunchBody({
        adAccountId: ctx.adAccountId,
        pageId: ctx.pageId,
        campaignName: prefill.campaignName?.trim() || defaultCampaignName(linkUrl),
        linkUrl: linkUrl.trim(),
        dailyBudget: budgetNumber,
        headline: headline.trim(),
        primaryText: primaryText.trim(),
        creatives: [selectedCreative],
      });
      const launchResult = await launchAdOverSSE(body, recordStep);
      setResult(launchResult);
      setPhase("success");
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : "Uruchomienie nie powiodło się.");
      setPhase("edit");
    }
  };

  if (phase === "success" && result) {
    return <SuccessScreen result={result} onRestart={onRestart} />;
  }

  if (phase === "launching") {
    return (
      <div className="space-y-3">
        <p className="text-sm text-white/60">Uruchamiam Twoją reklamę — to potrwa chwilę…</p>
        <LaunchProgress steps={launchSteps} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {launchError && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/[0.05] p-3 text-sm text-red-300">
          Nie udało się uruchomić reklamy: {launchError}
        </div>
      )}

      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
        <div>
          <Label className="text-xs text-white/60">Zdjęcie reklamy</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {allImages.map((img) => (
              <button
                key={img.url}
                type="button"
                onClick={() => setSelectedImageUrl(img.url)}
                className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                  selectedImageUrl === img.url
                    ? "border-[#ef3e29]"
                    : "border-white/10 hover:border-white/30"
                }`}
              >
                <img src={img.url} alt={img.name ?? ""} className="w-full h-full object-cover" />
                {selectedImageUrl === img.url && (
                  <CheckCircle2 className="absolute top-1 right-1 w-4 h-4 text-[#ef3e29] bg-black/60 rounded-full" />
                )}
              </button>
            ))}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-white/15 hover:border-white/40 flex flex-col items-center justify-center gap-1 text-white/40 hover:text-white/70 transition-colors"
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <ImagePlus className="w-5 h-5" />
              )}
              <span className="text-[10px]">Wgraj</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleUpload}
            />
          </div>
          {!selectedCreative && (
            <p className="text-[11px] text-white/40 mt-1.5">
              Wybierz jedno ze zdjęć albo wgraj własne — reklama musi mieć grafikę.
            </p>
          )}
        </div>

        <div>
          <Label className="text-xs text-white/60">Nagłówek</Label>
          <Input
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Krótki, mocny nagłówek"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-white/60">Tekst reklamy</Label>
          <Textarea
            value={primaryText}
            onChange={(e) => setPrimaryText(e.target.value)}
            placeholder="Co oferujesz i dla kogo to jest?"
            className="mt-1"
            rows={3}
          />
        </div>
        <div>
          <Label className="text-xs text-white/60">Dokąd prowadzi reklama (adres strony)</Label>
          <Input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://twojastrona.pl/oferta"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-white/60">Ile chcesz wydawać dziennie (zł)</Label>
          <Input
            type="number"
            min={1}
            value={dailyBudget}
            onChange={(e) => setDailyBudget(e.target.value)}
            className="mt-1 w-32"
          />
        </div>
      </div>

      <p className="text-[11px] text-white/40">
        Po kliknięciu reklama ruszy od razu po zatwierdzeniu przez Facebooka. Płacisz maksymalnie
        tyle, ile ustawisz dziennie — reklamę możesz w każdej chwili wstrzymać.
      </p>

      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-xs text-white/40 hover:text-white/70 flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Wstecz
        </button>
        <Button
          onClick={handleLaunch}
          disabled={!canLaunch}
          className="bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white disabled:opacity-40"
        >
          Uruchom reklamę <Rocket className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
